/**
 * TOTP API - For managing TOTP status for users
 */
import { apiClient } from "./client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface TotpStatus {
    isEnabled: boolean;
    hasSecret: boolean;
}

/**
 * Fetches TOTP status for a user
 */
export const fetchTotpStatus = async (userId: string): Promise<TotpStatus> => {
    const response = await apiClient.get<TotpStatus>(`/users/${userId}/totp-status`);
    return response.data;
};

/**
 * Resets TOTP for a user (admin only)
 */
export const resetTotp = async (userId: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/totp/reset`);
};

/**
 * React Query hook for fetching TOTP status
 */
export const useTotpStatus = (userId: string) => {
    return useQuery({
        queryKey: ["users", userId, "totp-status"],
        queryFn: () => fetchTotpStatus(userId),
        enabled: !!userId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * React Query hook for resetting TOTP
 */
export const useResetTotp = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: resetTotp,
        onSuccess: (_, userId) => {
            queryClient.invalidateQueries({ queryKey: ["users", userId, "totp-status"] });
            queryClient.invalidateQueries({ queryKey: ["users", userId] });
        },
    });
};